/**
 * WalletBridge — reactive glue between the active SDK adapter and web3-react.
 *
 * Rendered just inside `Web3ReactProvider`. It watches the adapter controller's
 * live provider + account and mirrors them into the singleton `EIP1193Connector`
 * via `bridgeProvider`. Because it keys off (provider, account), a single effect
 * covers every case with no special-casing:
 *   - fresh connect:        provider+account appear -> bridge
 *   - eager reconnect:      SDK restores a session  -> provider+account appear -> bridge
 *   - account switch:       account changes         -> re-bridge (new identity)
 *   - chain switch (churn): provider object changes -> re-bridge (adopt refreshes)
 *   - SDK-side disconnect:  provider/account clear  -> unbridge
 *
 * `useWallet.activate()` also bridges imperatively on click so it can await an
 * active connection before advancing the connect modal; `bridgeProvider` dedupes,
 * so the two paths never double-activate.
 *
 * For the default WEB3REACT kind this renders null and does nothing.
 */
import { useEffect, useRef } from "react";
import { WalletProviderKind } from "../../enums/walletProvider";
import { useWalletAdapter } from "../../utils/wallet/providers/context";
import {
  bridgeProvider,
  isBridged,
  unbridgeProvider,
} from "../../utils/wallet/providers/bridge";

export function WalletBridge() {
  const controller = useWalletAdapter();
  const { kind } = controller;

  // Stable identity references (adapters must keep getProvider() stable unless the
  // underlying provider genuinely changes — see WalletAdapter docs).
  const provider = controller.getProvider();
  const account = controller.getAccount();

  // Only bridge a genuine session. An SDK can surface a provider/account for a
  // wallet the user already disconnected (e.g. Privy logout() leaves MetaMask
  // injected) — hasSession() gates on the SDK's own authenticated state so we
  // never silently re-bridge a session the user ended. Falls back to
  // provider+account for adapters that don't implement hasSession.
  const hasSession = controller.hasSession
    ? controller.hasSession()
    : Boolean(provider && account);

  // Guards against acting on a stale async result after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (kind === WalletProviderKind.WEB3REACT) return;

    if (provider && account && hasSession) {
      bridgeProvider(provider, account).catch((err) => {
        if (mountedRef.current) {
          // eslint-disable-next-line no-console
          console.error("[WalletBridge] failed to bridge SDK provider", err);
        }
      });
    } else if (isBridged()) {
      // SDK reports no active session (logged out / disconnected elsewhere) —
      // tear down, even if a stale injected provider/account still lingers.
      unbridgeProvider().catch(() => {});
    }
    // provider/account are stable refs from the adapter; effect only re-runs on
    // a genuine change.
  }, [kind, provider, account, hasSession]);

  return null;
}

export default WalletBridge;
